import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "../../api/axios";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

function StatusChip({ value }) {
  const normalizedValue = String(value || "").toLowerCase();

  const colorMap = {
    uploaded: "info",
    processing: "warning",
    completed: "success",
    failed: "error",
    pending: "secondary",
    running: "warning",
  };

  const color = colorMap[normalizedValue] || "secondary";
  const label = value || "unknown";

  return (
    <MDBox px={1.5} py={0.5} borderRadius="lg" bgColor={color} color="white" display="inline-block">
      <MDTypography variant="caption" fontWeight="bold" color="white">
        {label}
      </MDTypography>
    </MDBox>
  );
}

StatusChip.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

StatusChip.defaultProps = {
  value: "",
};

export default function SystemSetupPage() {
  const [meta, setMeta] = useState({
    allowed_file_types: [],
    environment: "",
  });
  const [files, setFiles] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selectedType, setSelectedType] = useState("staff_upload");
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirmation, setConfirmation] = useState("");
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [workerInfo, setWorkerInfo] = useState({
    pending_jobs: 0,
    failed_jobs: 0,
  });

  const envLabel = useMemo(() => meta.environment || "unknown", [meta.environment]);

  const loadMeta = async () => {
    setLoadingMeta(true);
    try {
      const response = await axios.get("/admin/system-setup/meta");
      const payload = response?.data || {
        allowed_file_types: [],
        environment: "",
      };

      setMeta(payload);

      if (Array.isArray(payload.allowed_file_types) && payload.allowed_file_types.length > 0) {
        setSelectedType(payload.allowed_file_types[0]?.value || "staff_upload");
      }
    } catch (error) {
      console.error("Failed to load setup metadata:", error);
    } finally {
      setLoadingMeta(false);
    }
  };

  const loadFiles = async () => {
    setLoadingFiles(true);
    try {
      const response = await axios.get("/admin/system-setup/files");
      setFiles(response?.data?.data || []);
    } catch (error) {
      console.error("Failed to load uploaded files:", error);
      setFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  };

  const loadRuns = async () => {
    setLoadingRuns(true);
    try {
      const response = await axios.get("/admin/system-setup/setup-runs");
      setRuns(response?.data?.data || []);
    } catch (error) {
      console.error("Failed to load setup runs:", error);
      setRuns([]);
    } finally {
      setLoadingRuns(false);
    }
  };

  const loadWorker = async () => {
    try {
      const response = await axios.get("/admin/system-setup/worker-status");
      setWorkerInfo(
        response?.data || {
          pending_jobs: 0,
          failed_jobs: 0,
        }
      );
    } catch (error) {
      console.error("Failed to load worker info:", error);
      setWorkerInfo({
        pending_jobs: 0,
        failed_jobs: 0,
      });
    }
  };

  useEffect(() => {
    loadMeta();
    loadFiles();
    loadRuns();
    loadWorker();
  }, []);

  const handleUpload = async () => {
    if (!selectedFile) {
      alert("Please select a file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("file_type", selectedType);

    setSubmitting(true);

    try {
      await axios.post("/admin/system-setup/upload-file", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setSelectedFile(null);

      const fileInput = document.getElementById("system-setup-file-input");
      if (fileInput) {
        fileInput.value = "";
      }

      await loadFiles();
      alert("File uploaded successfully.");
    } catch (error) {
      console.error("Upload failed:", error);
      alert(error?.response?.data?.message || "Upload failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteFile = async (id) => {
    if (!window.confirm("Delete this uploaded file?")) {
      return;
    }

    setSubmitting(true);

    try {
      await axios.delete(`/admin/system-setup/files/${id}`);
      await loadFiles();
      alert("File deleted successfully.");
    } catch (error) {
      console.error("Delete failed:", error);
      alert(error?.response?.data?.message || "Delete failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetDatabase = async () => {
    if (!window.confirm("This will wipe the database and re-run migration. Continue?")) {
      return;
    }

    setSubmitting(true);

    try {
      await axios.post("/admin/system-setup/reset-database", {
        confirmation,
      });

      alert("Database reset completed successfully.");

      await Promise.all([loadFiles(), loadRuns(), loadWorker()]);
    } catch (error) {
      console.error("Database reset failed:", error);
      alert(error?.response?.data?.message || "Reset failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleProcess = async () => {
    setSubmitting(true);

    try {
      await axios.post("/admin/system-setup/process");
      alert("Setup processing started successfully.");

      await Promise.all([loadFiles(), loadRuns(), loadWorker()]);
    } catch (error) {
      console.error("Process start failed:", error);
      alert(error?.response?.data?.message || "Process start failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartWorker = async () => {
    setSubmitting(true);

    try {
      await axios.post("/admin/system-setup/start-worker");
      alert("Queue worker started successfully.");
      await loadWorker();
    } catch (error) {
      console.error("Worker start failed:", error);
      alert(error?.response?.data?.message || "Worker start failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MDBox py={3}>
      <MDBox mb={3}>
        <MDTypography variant="h4" fontWeight="bold">
          System Setup
        </MDTypography>
        <MDTypography variant="body2" color="text">
          Super Admin can reset the database, upload setup Excel files, and process the application
          setup from scratch.
        </MDTypography>
      </MDBox>

      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Card>
            <MDBox p={3}>
              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <Icon>settings_backup_restore</Icon>
                <MDTypography variant="h6">Database Initialization</MDTypography>
              </MDBox>

              <MDTypography variant="body2" color="text" mb={2}>
                Type <strong>RESET</strong> below before resetting the database.
              </MDTypography>

              <MDInput
                fullWidth
                label="Confirmation"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
              />

              <MDBox mt={2}>
                <MDButton
                  color="error"
                  onClick={handleResetDatabase}
                  disabled={submitting || confirmation !== "RESET"}
                >
                  {submitting ? "Processing..." : "Reset Database & Migrate"}
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card>
            <MDBox p={3}>
              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <Icon>memory</Icon>
                <MDTypography variant="h6">Queue Worker</MDTypography>
              </MDBox>

              <MDTypography variant="body2" color="text">
                Environment: <strong>{envLabel}</strong>
              </MDTypography>

              <MDTypography variant="body2" color="text">
                Pending Jobs: <strong>{workerInfo?.pending_jobs ?? 0}</strong>
              </MDTypography>

              <MDTypography variant="body2" color="text">
                Failed Jobs: <strong>{workerInfo?.failed_jobs ?? 0}</strong>
              </MDTypography>

              <MDBox mt={2}>
                <MDButton color="info" onClick={handleStartWorker} disabled={submitting}>
                  Start Queue Worker
                </MDButton>
              </MDBox>

              <MDBox mt={1}>
                <MDTypography variant="caption" color="text">
                  In production, the queue worker should run permanently in the background. This
                  button is mainly for local or development environments.
                </MDTypography>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                <Icon>upload_file</Icon>
                <MDTypography variant="h6">Upload Setup Files</MDTypography>
              </MDBox>

              {loadingMeta ? (
                <CircularProgress size={24} />
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="button" fontWeight="regular" mb={1} display="block">
                      File Type
                    </MDTypography>

                    <select
                      value={selectedType}
                      onChange={(event) => setSelectedType(event.target.value)}
                      style={{
                        width: "100%",
                        height: 42,
                        borderRadius: 8,
                        border: "1px solid #d2d6da",
                        padding: "0 12px",
                        outline: "none",
                        backgroundColor: "#fff",
                      }}
                    >
                      {meta.allowed_file_types.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <MDTypography variant="button" fontWeight="regular" mb={1} display="block">
                      Select File
                    </MDTypography>

                    <input
                      id="system-setup-file-input"
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
                      style={{ marginTop: 8 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <MDBox mt={{ xs: 0, md: 3 }}>
                      <MDButton
                        color="success"
                        fullWidth
                        onClick={handleUpload}
                        disabled={submitting}
                      >
                        {submitting ? "Uploading..." : "Upload File"}
                      </MDButton>
                    </MDBox>
                  </Grid>
                </Grid>
              )}

              <Divider sx={{ my: 3 }} />

              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="h6">Uploaded Files Awaiting Processing</MDTypography>
                <MDButton color="dark" onClick={loadFiles} disabled={loadingFiles}>
                  Refresh
                </MDButton>
              </MDBox>

              {loadingFiles ? (
                <CircularProgress size={24} />
              ) : files.length === 0 ? (
                <MDTypography variant="body2" color="text">
                  No uploaded files found.
                </MDTypography>
              ) : (
                <MDBox>
                  {files.map((file) => (
                    <MDBox
                      key={file.id}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={2}
                      p={2}
                      mb={1}
                      border="1px solid #eee"
                      borderRadius="lg"
                    >
                      <MDBox>
                        <MDTypography variant="button" fontWeight="medium">
                          {file.original_name}
                        </MDTypography>
                        <MDTypography variant="caption" display="block" color="text">
                          Type: {file.file_type}
                        </MDTypography>
                      </MDBox>

                      <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <StatusChip value={file.status} />
                        <MDButton
                          color="error"
                          variant="outlined"
                          size="small"
                          onClick={() => handleDeleteFile(file.id)}
                          disabled={submitting}
                        >
                          Remove
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  ))}
                </MDBox>
              )}

              <MDBox mt={3}>
                <MDButton
                  color="info"
                  onClick={handleProcess}
                  disabled={submitting || files.length === 0}
                >
                  {submitting ? "Processing..." : "Process Setup Files"}
                </MDButton>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <MDBox p={3}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="h6">Setup Run History</MDTypography>
                <MDButton color="dark" onClick={loadRuns} disabled={loadingRuns}>
                  Refresh
                </MDButton>
              </MDBox>

              {loadingRuns ? (
                <CircularProgress size={24} />
              ) : runs.length === 0 ? (
                <MDTypography variant="body2" color="text">
                  No setup runs yet.
                </MDTypography>
              ) : (
                runs.map((run) => (
                  <MDBox key={run.id} p={2} mb={1} border="1px solid #eee" borderRadius="lg">
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={2}
                    >
                      <MDBox>
                        <MDTypography variant="button" fontWeight="medium">
                          Setup Run #{run.id}
                        </MDTypography>
                        <MDTypography variant="caption" display="block" color="text">
                          Files: {run.files_count || 0}
                        </MDTypography>
                      </MDBox>

                      <StatusChip value={run.status} />
                    </MDBox>
                  </MDBox>
                ))
              )}
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
